import { useState, useEffect, useCallback } from "react";
import { Link, useNavigate, useLocation } from "react-router-dom";
import { useSiteContent } from "@/cms/SiteContentContext";
import { editableAttrs } from "@/cms/previewUtils";
import { MAPS_DIRECTIONS_URL } from "@/config/maps";

const Navbar = () => {
  const [mobileOpen, setMobileOpen] = useState(false);
  const navigate = useNavigate();
  const location = useLocation();
  const { content } = useSiteContent();
  const nav = content.navigation;

  const handleScroll = useCallback(() => {
    // Scroll listener intentionally kept for future visual states.
  }, []);

  useEffect(() => {
    window.addEventListener("scroll", handleScroll, { passive: true });
    return () => window.removeEventListener("scroll", handleScroll);
  }, [handleScroll]);

  useEffect(() => {
    setMobileOpen(false);
    window.scrollTo({ top: 0, behavior: "smooth" });
  }, [location.pathname]);

  return (
    <header className="fixed top-0 left-0 right-0 z-50 transition-all duration-300 bg-zinc-950/95 backdrop-blur-sm border-b border-zinc-800 shadow-lg shadow-black/20 min-h-16 md:min-h-20">
      <div className="max-w-7xl mx-auto px-3 sm:px-4 md:px-6 h-16 md:h-20 flex items-stretch justify-between">
        <Link to="/" className="h-full flex items-center cursor-pointer" {...editableAttrs("navigation.logoSrc", "image")}>
          <img
            src={nav.logoSrc}
            alt={nav.logoAlt}
            className="h-8 sm:h-9 md:h-12 w-auto max-h-full object-contain"
            {...editableAttrs("navigation.logoSrc", "image")}
          />
        </Link>

        <nav className="hidden md:flex h-full items-center gap-7" {...editableAttrs("navigation.links", "array")}>
          {nav.links.map((link, index) => {
            const isActive = location.pathname === link.href;
            return (
              <Link
                key={link.href}
                to={link.href}
                className={`text-sm leading-none font-medium tracking-wide transition-colors duration-200 whitespace-nowrap cursor-pointer ${
                  isActive ? "text-red-500" : "text-zinc-300 hover:text-white"
                }`}
                style={{ fontFamily: "Rajdhani, sans-serif", letterSpacing: "0.08em" }}
                {...editableAttrs(`navigation.links.${index}.label`, "text")}
              >
                {link.label}
              </Link>
            );
          })}
        </nav>

        <div className="hidden md:flex self-center items-center gap-2">
          <a
            href={nav.instagramUrl}
            target="_blank"
            rel="noopener noreferrer"
            aria-label="Instagram"
            className="w-10 h-10 flex items-center justify-center border border-zinc-700 text-zinc-300 hover:text-white hover:border-red-600 transition-all duration-200"
            {...editableAttrs("navigation.instagramUrl", "url")}
          >
            <i className="ri-instagram-line text-lg"></i>
          </a>
          <a
            href={nav.whatsappUrl}
            target="_blank"
            rel="noopener noreferrer"
            aria-label="WhatsApp"
            className="w-10 h-10 flex items-center justify-center border border-zinc-700 text-zinc-300 hover:text-white hover:border-red-600 transition-all duration-200"
            {...editableAttrs("navigation.whatsappUrl", "url")}
          >
            <i className="ri-whatsapp-line text-lg"></i>
          </a>
          <button
            onClick={() => navigate("/iletisim")}
            className="flex items-center justify-center h-10 gap-2 bg-red-600 hover:bg-red-700 text-white px-5 text-sm leading-none font-semibold tracking-wider transition-all duration-200 cursor-pointer whitespace-nowrap"
            style={{ fontFamily: "Rajdhani, sans-serif", letterSpacing: "0.1em" }}
            {...editableAttrs("navigation.ctaLabel", "text")}
          >
            <i className="ri-calendar-check-line text-base"></i>
            {nav.ctaLabel}
          </button>
          <a
            href={MAPS_DIRECTIONS_URL}
            target="_blank"
            rel="noopener noreferrer"
            className="flex items-center justify-center h-10 gap-2 border border-zinc-600 hover:border-red-600 text-zinc-200 hover:text-white px-5 text-sm leading-none font-semibold tracking-wider transition-all duration-200 whitespace-nowrap"
            style={{ fontFamily: "Rajdhani, sans-serif", letterSpacing: "0.1em" }}
            {...editableAttrs("navigation.routeCtaLabel", "text")}
          >
            <i className="ri-route-line text-base"></i>
            {nav.routeCtaLabel}
          </a>
        </div>

        <div className="md:hidden flex flex-1 min-w-0 justify-end items-center gap-1 pr-1">
          <a
            href={nav.instagramUrl}
            target="_blank"
            rel="noopener noreferrer"
            aria-label="Instagram"
            className="w-8 h-8 sm:w-9 sm:h-9 flex items-center justify-center border border-zinc-700 text-zinc-300 hover:text-white hover:border-red-600 transition-all duration-200 flex-shrink-0"
          >
            <i className="ri-instagram-line text-base sm:text-lg"></i>
          </a>
          <a
            href={nav.whatsappUrl}
            target="_blank"
            rel="noopener noreferrer"
            aria-label="WhatsApp"
            className="w-8 h-8 sm:w-9 sm:h-9 flex items-center justify-center border border-zinc-700 text-zinc-300 hover:text-white hover:border-red-600 transition-all duration-200 flex-shrink-0"
          >
            <i className="ri-whatsapp-line text-base sm:text-lg"></i>
          </a>
          <a
            href={MAPS_DIRECTIONS_URL}
            target="_blank"
            rel="noopener noreferrer"
            aria-label="Yol tarifi al"
            className="w-8 h-8 sm:w-9 sm:h-9 flex items-center justify-center border border-zinc-700 text-zinc-300 hover:text-white hover:border-red-600 transition-all duration-200 flex-shrink-0"
          >
            <i className="ri-route-line text-base sm:text-lg"></i>
          </a>
        </div>

        <button
          className="md:hidden self-center w-8 h-8 sm:w-9 sm:h-9 flex-shrink-0 flex items-center justify-center text-white cursor-pointer"
          onClick={() => setMobileOpen((v) => !v)}
          aria-label="Menü"
        >
          <i className={`text-xl ${mobileOpen ? "ri-close-line" : "ri-menu-line"}`}></i>
        </button>
      </div>

      <div
        className={`md:hidden transition-all duration-300 overflow-hidden bg-zinc-950/98 border-b border-zinc-800 ${
          mobileOpen ? "max-h-[min(32rem,85vh)] opacity-100" : "max-h-0 opacity-0"
        }`}
      >
        <div className="px-6 py-4 flex flex-col gap-1">
          {nav.links.map((link) => {
            const isActive = location.pathname === link.href;
            return (
              <Link
                key={link.href}
                to={link.href}
                className={`text-base font-semibold py-2.5 border-b border-zinc-800 text-left cursor-pointer transition-colors whitespace-nowrap ${
                  isActive ? "text-red-500" : "text-zinc-300 hover:text-red-500"
                }`}
                style={{ fontFamily: "Rajdhani, sans-serif" }}
              >
                {link.label}
              </Link>
            );
          })}
          <Link
            to="/iletisim"
            className="mt-3 bg-red-600 text-white py-3 text-sm font-bold tracking-wider cursor-pointer whitespace-nowrap text-center"
            style={{ fontFamily: "Rajdhani, sans-serif" }}
          >
            {nav.ctaLabel}
          </Link>
        </div>
      </div>
    </header>
  );
};

export default Navbar;
